import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LightboxesService } from '../components/lightboxes/lightboxes.service';
import { ProdutoService } from '../services/produto.service';
import { OperadoraState } from '../states/operadora.state';
import { PlanoState } from '../states/plano.state';
import { ProdutoState } from '../states/produto.state';
import { ICategoriaDeProduto } from '../types/ICategoriaDeProduto';
import { IGetPostBase64Params } from '../types/IGetPostBase64Params';
import { downloadBase64 } from '../../utils/downloadBase64';
import { IProduto } from '../types/IProduto';
import { IPlano } from '../types/IPlano';

@Injectable({ providedIn: 'root' })
export class ProdutoFacade {

  constructor(
    private produtoService: ProdutoService,
    private produtoState: ProdutoState,
    private planoState: PlanoState,
    private operadoraState: OperadoraState,
    private lightboxesService: LightboxesService,
    private router: Router,
  ) { }

  requestProdutos() {
    this.produtoState.setRequestingProdutos(true);

    this.produtoService.getProdutos().subscribe(
      response => this.produtoState.setProdutos(response),
      error => this.handleError(error.error),
      () => this.produtoState.setRequestingProdutos(false),
    );
  }

  requestProdutosIfUnset() {
    if (this.produtoState.isEmpty())
      this.requestProdutos();
  }

  requestDownloadPostImage(postID: number) {
    const params: IGetPostBase64Params = {
      idImagem: postID,
      idPlano: this.planoState.getPlanoSelecionado().id,
      idProduto: this.produtoState.getProdutoSelecionado().id,
      idProdutoPlano: this.operadoraState.getOperadoraSelecionada().id,
    };

    this.produtoState.setRequestingPostDownload(true);

    this.produtoService.getPostBase64(params).subscribe(
      (response: any) => downloadBase64(response.arquivo, response.descricao, 'image/png'),
      error => this.handleError(error.error),
      () => this.produtoState.setRequestingPostDownload(false),
    );
  }

  reset() {
    this.produtoState.reset();
  }

  setProdutoSelecionado(produto: ICategoriaDeProduto) {
    this.produtoState.setProdutoSelecionado(produto);
  }

  getProdutoSelecionado() {
    return this.produtoState.getProdutoSelecionado();
  }

  hasProdutoSelecionado() {
    return this.produtoState.hasProdutoSelecionado();
  }

  getProdutos() {
    return this.produtoState.getProdutos();
  }

  getHomeIcon(name: string) {
    return `../../../../../assets/icons/home/${name}.png`;
  }

  getSidebarIcon(name: string) {
    return `../../../../../assets/icons/sidebar/${name}.png`;
  }

  isRequestingProdutos() {
    return this.produtoState.isRequestingProdutos();
  }

  isRequestingPostDownload() {
    return this.produtoState.isRequestingPostDownload();
  }

  handleProdutoClick(produto: ICategoriaDeProduto) {
    switch (produto.nome) {
      case 'Meus Dados': this.router.navigate(['/meus-dados']); break;
      case 'Fale Conosco': this.router.navigate(['/fale-conosco']); break;
      case 'Cotação': this.router.navigate(['/cotacao']); break;
      case 'Premiações': this.router.navigate(['/area-premiacoes']); break;
      case 'Biblioteca de Vídeos': this.router.navigate(['/biblioteca-de-videos']); break;
      case 'Planos Odontológicos':
      case 'Planos Médicos':
        this.produtoState.setProdutoSelecionado(produto);
        this.router.navigate([`/planos/${produto.nome}`]);
        break;

      default:
        if (produto.url)
          window.open(produto.url, '__blank')
    }
  }

  setPlanoOdontologicoSelecionado(planoAProcurar: string, produtoAProcurar: string) {
    const produtos = this.produtoState.getCurrentProdutos();

    const odontologicoIndex = produtos.findIndex(produto => {
      if (produto.nome === 'Planos Odontológicos')
        return produto;
    });

    if (odontologicoIndex === -1) {
      this.handleError('Produto não encontrado.');
      return;
    }

    const planoASelecionar = produtos[odontologicoIndex].planos.find(plano => {
      if (plano.nome === planoAProcurar)
        return plano;
    });

    if (planoASelecionar === undefined) {
      this.handleError('Plano não encontrado.');
      return;
    }

    const produtoASelecionar = planoASelecionar.produtos.find(produto => {
      if (produto.nome === produtoAProcurar)
        return produto;
    });

    if (produtoASelecionar === undefined) {
      this.handleError('Produto não encontrado.');
      return;
    }

    this.planoState.selectPlano(planoASelecionar);
    this.setProdutoSelecionado(produtos[odontologicoIndex]);
    this.operadoraState.selectOperadora(produtoASelecionar);
  }

  redirecionarParaPaginaDeUmProduto(idProduto: number, idPlano?: number, idOperadora?: number) {
    this.setProdutosSelecionadoById(idProduto, idPlano, idOperadora);
    let urlToRoute: string;
    let pathPrefix = '';
    let pathSuffix = '';

    const mapIdToPathSuffix = {
      1: 'Planos Odontológicos',
      2: 'Planos Medicos',
    };

    pathSuffix = mapIdToPathSuffix[idProduto];

    if(idPlano === undefined && idOperadora === undefined)
      pathPrefix = 'planos/';

    if(idPlano !== undefined)
      pathPrefix = 'operadoras/';

    if(idOperadora !== undefined)
      pathPrefix= 'material/';

    urlToRoute = '/' + pathPrefix + pathSuffix;

    window.setTimeout(() => {
      sessionStorage.setItem('selectedMenu', idProduto.toString())
      this.router.navigate([urlToRoute]);
    }, 200)
  }
  
  setProdutosSelecionadoById(idProduto: number, idPlano?: number, idOperadora?: number) {
    const produto = this.procurarPorProdutoComID(idProduto);
    let plano: IPlano;
    let operadora: IProduto;

    if(idPlano !== undefined)
      plano = this.procurarPlanoComID(idPlano, produto.id);

    if(idPlano !== undefined && idOperadora !== undefined)
      operadora = this.procurarProdutoComID(idOperadora, produto.id, plano.id);

    this.produtoState.setProdutoSelecionado(produto);
    this.planoState.selectOriginalPlano(plano);
    this.operadoraState.setOriginalState(operadora);
  }

  public procurarPorProdutoComID(idProduto: number) {
    const produtos = this.produtoState.getCurrentProdutos();

    return produtos.find(produto => produto.id === idProduto);
  }

  public procurarPlanoComID(idPlano: number, idDoProdutoAFazerABusca: number) {
    const produto = this.produtoState.getCurrentProdutos().find(p => p.id === idDoProdutoAFazerABusca);

    return produto.planos.find(operadora => operadora.id === idPlano);
  }

  public procurarProdutoComID(idPlano: number, idDoProdutoAFazerABusca: number, idDaOperadoraAFazerABusca: number) {
    const produto = this.produtoState.getCurrentProdutos().find(p => p.id === idDoProdutoAFazerABusca);
    const operadora = produto.planos.find(o => o.id === idDaOperadoraAFazerABusca);

    return operadora.produtos.find(plano => plano.id === idPlano);
  }

  private handleError(errorMsg: string) {
    this.lightboxesService.open({
      title: 'Atenção',
      text: errorMsg + ' Tente novamente mais tarde.',
    }, 30_000);
  }
}